import { motion } from 'framer-motion';
import React from 'react';

import { useWindowDimensions } from '../../hooks/useDimensions';
import { ContentPushWrapper } from './AIInsightsSidebar.styles';
import { useAIInsightsSidebar } from './AIInsightsSidebarContext';

const MotionContentPushWrapper = motion(ContentPushWrapper);

interface AIInsightsSidebarContentPushProps {
  children: React.ReactNode;
}

/**
 * Wraps page content and applies padding-right equal to the sidebar width
 * on desktop when the sidebar is open, so content is pushed rather than overlapped.
 * On tablet and below the sidebar overlays, so no padding is applied.
 */
export const AIInsightsSidebarContentPush = ({ children }: AIInsightsSidebarContentPushProps) => {
  const ctx = useAIInsightsSidebar();
  const { isInLarge } = useWindowDimensions();

  if (!ctx) return <>{children}</>;

  const { isOpen, isExpanded, isExpandFading, sidebarWidth, isResizing, skipExpandAnimation } = ctx;

  // Only push content on large+ screens (sidebar overlays on smaller screens)
  const targetPaddingRight = !isInLarge && isOpen && !isExpanded ? sidebarWidth : 0;

  const paddingTransition = () => {
    if (isResizing || skipExpandAnimation) return { duration: 0 };
    return { duration: 0.3, ease: 'easeOut' as const };
  };

  if (skipExpandAnimation) {
    return (
      <MotionContentPushWrapper style={{ paddingRight: targetPaddingRight, opacity: 1 }} animate={{}}>
        {children}
      </MotionContentPushWrapper>
    );
  }

  return (
    <MotionContentPushWrapper
      animate={{ paddingRight: targetPaddingRight, opacity: isExpandFading ? 0 : 1 }}
      transition={{
        paddingRight: paddingTransition(),
        opacity: { duration: 0.3, ease: 'easeInOut' },
      }}
    >
      {children}
    </MotionContentPushWrapper>
  );
};
